import { useEffect } from 'react';

import { X } from 'lucide-react';

type Props = {
  src: string;
  alt: string;
  onClose: () => void;
};

/**
 * Feature 4 (2026-08) — lightbox plein écran pour afficher une image (ex.
 * l'affiche d'un concours). Fermeture via la touche Échap, le bouton, ou un
 * clic sur le fond. Bloque le scroll du body tant qu'elle est ouverte.
 */
export default function ImageLightbox({ src, alt, onClose }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      className='fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 md:p-10'
      role='dialog'
      aria-modal='true'
      aria-label={alt}
      onClick={onClose}
    >
      <button
        type='button'
        onClick={onClose}
        aria-label='Fermer'
        className='absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20'
      >
        <X className='h-6 w-6' />
      </button>
      <img
        src={src}
        alt={alt}
        onClick={e => e.stopPropagation()}
        className='max-h-full max-w-full rounded-2xl object-contain shadow-2xl z-40'
      />
    </div>
  );
}
